// Reference: https://firebase.google.com/docs/reference/js/firebase.messaging

import messaging from '../util/messaging';
import ms        from 'ms';
import optIn     from './opt_in';
import rollbar   from '../rollbar';


let registering = null;


// Returns true if Firebase supported on this platform.
export function isSupported() {
  return !!messaging;
}


export async function start({ onToken, onNotification, onDataMessage, onGranted, onDenied }) {
  handleMessages({ onNotification, onDataMessage });
  // This may be called multiple times, if two requests are trying to register
  // the Service Worker, Firebase gets confused and bails.  So make sure we only
  // register once.
  if (!registering)
    registering = registerServiceWorker();
  await registering;

  await requestPermission({ onToken, onGranted, onDenied });
}


export async function stop() {
  try {
    await messaging.deleteToken();
  } catch (error) {
    rollbar.error(error);
  }
}


async function registerServiceWorker() {
  const registration = await navigator.serviceWorker.register('/sw.js');

  // Old app had its own service worker.
  if (typeof registration.update === 'function') {
    try {
      await registration.update();
    } catch (error) {
      rollbar.error(error);
    }
  }

  await messaging.useServiceWorker(registration);

  // If app is open, update Service Worker every hour.  The browser will also
  // attempt to refresh on push events (but only if there are push events), sync
  // events (we don't do that yet), and ignore cache if the last update was more
  // than 24 hours ago.
  //
  // See https://developers.google.com/web/fundamentals/primers/service-workers/lifecycle#manual_updates
  setInterval(function() {
    registration.update();
  }, ms('1h'));
}


async function requestPermission({ onToken, onGranted, onDenied }) {
  const initialPermissionStatus = Notification.permission;

  if (Notification.permission === 'default') {
    // Assume a denied behavior until granted.
    await onDenied();
    const isAccepted = await optIn();
    if (!isAccepted)
      return;
    try {
      await messaging.requestPermission();
    } catch (error) {
      const isIgnored = (error.code === 'messaging/permission-default');
      const isDenied  = (error.code === 'messaging/permission-blocked');
      if (!(isIgnored || isDenied))
        throw error;
    }
  }

  if (Notification.permission === 'granted') {
    try {
      await getDeviceToken(onToken);
      await onGranted();
    } catch (error) {
      // See https://github.com/broadly/broadly/issues/2742.
      rollbar.error(error);
      rollbar.info('Permission is granted, but Firebase failed to get a token', { initialPermissionStatus });
      await onDenied();
    }
  } else
    await onDenied();
}


async function getDeviceToken(onToken) {
  await updateDeviceToken();
  messaging.onTokenRefresh(() => updateDeviceToken().catch(rollbar.error));

  async function updateDeviceToken() {
    if (!localStorage.getItem('firebaseTokenRefreshed')) {
      await messaging.deleteToken();
      localStorage.setItem('firebaseTokenRefreshed', (new Date()).toISOString());
    }

    const deviceToken = await messaging.getToken();
    if (deviceToken)
      onToken(deviceToken);
  }
}


function handleMessages({ onNotification, onDataMessage }) {
  // Message arrives while app is in foreground.  It may be a notification
  // message (show in-app) or a data message.  Firebase forwards the message
  // to this function.
  messaging.onMessage(function({ notification, data }) {
    if (notification) {
      const { title, body } = notification;
      const url             = notification.click_action || data.url;
      onNotification({ title, body, url });
    }

    if (data)
      onDataMessage(data);
  });

  // Message arrives while app in background, and is a data message.
  // (Notification messages displayed by OS/Firebase)
  // sw.js forwards us the message by calling client.postMessage().
  navigator.serviceWorker.addEventListener('message', function({ data }) {
    if (data)
      onDataMessage(data);
  });
}

